import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { api } from '../../config/api';

const money = (value) => `£${(parseFloat(value) || 0).toFixed(2)}`;

const AnalysisHub = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/statistics/categories', {
          params: {
            startDate: `${year}-01-01`,
            endDate: `${year}-12-31`,
          },
        });
        const rows = (res.data.data || [])
          .map((item) => ({
            id: item.Category?.id,
            name: item.Category?.name || 'Uncategorized',
            color: item.Category?.color || '#9E9E9E',
            amount: parseFloat(item.totalAmount || 0),
            count: parseInt(item.count || 0, 10),
            avgAmount: parseFloat(item.avgAmount || 0),
          }))
          .filter((row) => row.id != null && row.amount > 0)
          .sort((a, b) => b.amount - a.amount);
        setCategories(rows);
      } catch (err) {
        console.error('Error loading analysis hub:', err);
        setError('Failed to load category analysis.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [year]);

  const yearTotal = categories.reduce((sum, row) => sum + row.amount, 0);
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="analysis-page">
      <div className="page-header">
        <div>
          <p className="eyebrow">Insights</p>
          <h1>Analysis</h1>
          <p className="text-muted mb-0">
            Drill into a category to see spend over the year — for example water, groceries, or bills.
          </p>
        </div>
        <Form.Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ width: '8rem' }}
          aria-label="Select year"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Form.Select>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="balance-strip mb-4">
        <div className="summary-tile">
          <div className="label mb-2">{year} category spend</div>
          <div className="value">{money(yearTotal)}</div>
          <div className="small text-muted mt-2">{categories.length} categories with spend</div>
        </div>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2 className="h5 mb-0">Categories</h2>
        </div>
        <div className="panel-body pt-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : categories.length === 0 ? (
            <p className="text-muted mb-0 py-3">No categorized spend for {year}.</p>
          ) : (
            <ul className="analysis-category-list mb-0">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/analysis/categories/${cat.id}?year=${year}`}
                    className="analysis-category-row"
                  >
                    <span
                      className="analysis-category-swatch"
                      style={{ background: cat.color }}
                      aria-hidden="true"
                    />
                    <span className="analysis-category-copy">
                      <strong>{cat.name}</strong>
                      <small>
                        {cat.count} expense{cat.count === 1 ? '' : 's'} · avg {money(cat.avgAmount)}
                      </small>
                    </span>
                    <span className="analysis-category-amount">{money(cat.amount)}</span>
                    <FontAwesomeIcon icon={faArrowRight} className="analysis-category-chevron" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <p className="small text-muted mt-3 mb-0">
        <FontAwesomeIcon icon={faChartLine} className="me-2" />
        Household overview lives on{' '}
        <Link to="/statistics">Statistics</Link>. Splits are chosen when an expense is created —
        analysis here is about spend totals, not “left to split.”
      </p>
    </div>
  );
};

export default AnalysisHub;
